import React from "react";
import { format } from "date-fns";
import SearchInput from "src/components/widgets/SearchInput";
import PaymentDetailModal from "src/components/modals/PaymentDetailModal";

import { cn, getCustomizationValue, renderCurrency } from "src/lib/utils";
import { useSearchParams } from "react-router-dom";
import { Button } from "src/components/ui/button";
import CustomDataTable from "src/components/CustomDataTable";
import { useQuery } from "@tanstack/react-query";
import { getPayments } from "src/api/payments";
import CustomSheet from "src/components/CustomSheet";
import { cva } from "class-variance-authority";
import withPermissionWrapper from "src/hooks/withPermissionWrapper";
import { useAuth } from "src/providers/AuthProvider";

const statusVariants = cva("tw-capitalize tw-font-semibold", {
	variants: {
		status: {
			pending: "tw-text-orange",
			success: "tw-text-green",
		},
	},
});

const columns = (user) => [
	{
		name: "S/N",
		selector: (_, index) => index + 1,
		width: "70px",
	},
	{
		name: cn(getCustomizationValue(user, "customer_label", true), "Name"),
		selector: (row) => `${row.first_name} ${row.last_name}`,
		grow: 1.5,
	},
	{
		name: getCustomizationValue(user, "invoice_label", true),
		selector: (row) => row.invoice_number,
	},
	{
		name: "Payment Method",
		selector: (row) => row.payment_method,
		style: { textTransform: "capitalize" },
	},
	{
		name: "Amount Paid",
		selector: (row) => renderCurrency(row.amount),
	},
	{
		name: "Payment Date",
		selector: (row) => format(new Date(row.created_at), "MMM dd, yyyy"),
	},
	{
		name: "Status",
		cell: (row) => <span className={statusVariants({ status: row.status })}>{row.status}</span>,
		grow: 0.75,
	},
	{
		name: "Action",
		cell: (row) => (
			<CustomSheet
				title="Payment Details"
				className="!tw-max-w-[40rem]"
				trigger={
					<Button variant="link" className="!tw-px-0">
						View
					</Button>
				}
			>
				<PaymentDetailModal paymentId={row.invoice_uuid} />
			</CustomSheet>
		),
		width: "100px",
	},
];

const Payments = () => {
	const { user } = useAuth();
	const [searchParams] = useSearchParams();
	const search = searchParams.get("search");
	const page = searchParams.get("page") || 1;
	const limit = searchParams.get("limit") || 10;

	const { data, isLoading } = useQuery({
		queryKey: ["payments", page, limit, search],
		queryFn: () => getPayments({ page, limit, search }),
	});

	const payments = data?.data?.data ?? [];
	const totalRow = data?.data?.total;

	return (
		<div className="pageWrapper">
			<div className="mainContent">
				<div className="pageTop">
					<h2>Payments</h2>
				</div>

				<div className="innerContent">
					<ActionBar />

					<CustomDataTable columns={columns(user)} data={payments} progressPending={isLoading} paginationTotalRows={totalRow} />
				</div>
			</div>
		</div>
	);
};

const ActionBar = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const search = searchParams.get("search");

	const updateSearchParams = (key, value) => {
		const newSearchParams = new URLSearchParams(searchParams);

		newSearchParams.set(key, value);

		setSearchParams(newSearchParams);
	};

	return (
		<div className="inner-top tw-flex-wrap tw-justify-end tw-gap-3">
			<SearchInput
				name="search_text"
				type="text"
				placeholder="Search..."
				value={search}
				onChange={(event) => updateSearchParams("search", event.target.value)}
			/>
		</div>
	);
};

const Comp = withPermissionWrapper(Payments, "view_payment");
export default Comp;
